import {takeEvery, take, put, fork, all, call, takeLatest, takeLeading, debounce} from 'redux-saga/effects'
import {eventChannel} from 'redux-saga'
import flBusiness from '@framelink/flbusiness'
import api from 'utils/api'
import warning from 'utils/enums/messages'
import operation from 'utils/enums/operation'
import login from 'modules/login'
import applications from 'modules/applications'
import entities from 'modules/entities'
import collection from 'modules/collection'
import linkedfield from 'modules/linkedfield'
import modules from 'modules/modules'
import relations from 'modules/relations'
import plannings from 'modules/plannings'
import record from 'modules/record'
import excel from 'modules/excel'
import publictables from 'modules/publictables'
import publicforms from 'modules/publicforms'
import sms from 'modules/sms'
import email from 'modules/email'
import exportdata from 'modules/exportdata'
import statistics from 'modules/statistics'
import messages from 'modules/messages'
import pdf from 'modules/pdf'
import scheduler from 'modules/scheduler'

const SEARCH_REQUEST_DELAY = 300

function* fetchInstances() {
    try {
        const list = yield call(api.fetchInstances)
		const instance = yield call(api.getDefaultInstance)
        yield put(login.actions.fetchInstancesSuccess(list, instance))
    } catch (error) {
        yield put(login.actions.fetchInstancesFailed())
    }
}

function* watchFetchInstances() {
    yield takeEvery(login.types.INSTANCES_FETCH, fetchInstances)
}

function* fetchLogin({username, password, instance}) {
    try {
        const data = yield call(api.fetchLogin, {username, password, instance})
		const apps = yield call(api.getApplications)
        const logo = yield call(api.getApplicationLogo)
		const tables = yield call(api.fetchEntities)

        yield put(login.actions.fetchLoginSuccess(data))
		yield put(applications.actions.getApplications(apps, logo))
		yield put(entities.actions.fetchEntitiesSuccess(tables))
    } catch (error) {
		yield put(messages.actions.messageSet(warning.loginError))
        yield put(login.actions.fetchLoginFailed())
    }
}

function* watchFetchLogin() {
    yield takeLatest(login.types.LOGIN_FETCH, fetchLogin)
}

function* fetchRegistration({newusername, newpassword, instance}) {
    try {
        const data = yield call(api.fetchRegistration, {newusername, newpassword, instance})
        yield put(login.actions.fetchRegistrationSuccess(data))
    } catch (error) {
        let failure = 'registrationError'
        if (error === flBusiness.Enum.ErrorCode.UserExists) failure = 'userExists'
		yield put(messages.actions.messageSet(warning[failure]))
        yield put(login.actions.fetchRegistrationFailed())
    }
}

function* watchFetchRegistration() {
    yield takeLatest(login.types.REGISTRATION_FETCH, fetchRegistration)
}

function* fetchAutologin() {
    try {
        const data = yield call(api.fetchAutologin)
		const apps = yield call(api.getApplications)
        const logo = yield call(api.getApplicationLogo)
		const tables = yield call(api.fetchEntities)

        yield put(login.actions.fetchAutologinSuccess(data))
		yield put(applications.actions.getApplications(apps, logo))
		yield put(entities.actions.fetchEntitiesSuccess(tables))
    } catch (error) {
        yield put(login.actions.fetchAutologinFailed())
    }
}

function* watchFetchAutologin() {
    yield takeLeading(login.types.AUTOLOGIN_FETCH, fetchAutologin)
}

function* fetchChangeApplication({application}) {
    try {
        const userinfo = yield call(api.changeApplication, application)
        const apps = yield call(api.getApplications)
        const logo = yield call(api.getApplicationLogo)
		const data = yield call(api.fetchModules)
		const tables = yield call(api.fetchEntities)

        yield put(modules.actions.fetchModulesSuccess(data))
        yield put(login.actions.fetchLoginSuccess(userinfo))
        yield put(applications.actions.changeApplicationSuccess(apps, logo))
		yield put(entities.actions.fetchEntitiesSuccess(tables))
    } catch (error) {
        yield put(applications.actions.changeApplicationFailed())
    }
}

function* watchFetchChangeApplication() {
    yield takeEvery(applications.types.CHANGE_APPLICATION_FETCH, fetchChangeApplication)
}

function* fetchCallUsers() {
    try {
        const contacts = yield call(api.getCallUsers)
        yield put(applications.actions.fetchCallUsersSuccess(contacts))
    } catch (error) {
        yield put(applications.actions.fetchCallUsersFailed())
    }
}

function* watchFetchCallUsers() {
    yield takeEvery(applications.types.CALL_USERS_FETCH, fetchCallUsers)
}

function* fetchResizeColumn({field, width}) {
    try {
        yield call(api.fetchResizeColumn, {field, width})
        yield put(collection.actions.updateViewStateSuccess())
    } catch (error) {
        yield put(collection.actions.updateViewStateFailed())
    }
}

function* watchFetchResizeColumn() {
    yield takeEvery(collection.types.RESIZE_COLUMN_FETCH, fetchResizeColumn)
}

function* fetchReorderColumns({columns}) {
    try {
        yield call(api.fetchReorderColumns, {columns})
        yield put(collection.actions.updateViewStateSuccess())
    } catch (error) {
        yield put(collection.actions.updateViewStateFailed())
    }
}

function* watchFetchReorderColumns() {
    yield takeEvery(collection.types.REORDER_COLUMNS_FETCH, fetchReorderColumns)
}

function* fetchLogout() {
    try {
        yield call(api.fetchLogout)
        yield put(login.actions.fetchLogoutSuccess())
    } catch (error) {
        yield put(login.actions.fetchLogoutFailed())
    }
}

function* watchFetchLogout() {
    yield takeEvery(login.types.LOGOUT_FETCH, fetchLogout)
}

function* initCollection({id, page, record}) {
    try {
        const data = yield call(api.initCollection, {id, page, record})
        const views = yield call(api.fetchViews, {id, page})
        yield put(collection.actions.collectionSuccess(data, views))
    } catch (error) {
        yield put(collection.actions.collectionFailed())
    }
}

function* watchInitCollection() {
    yield takeLatest(collection.types.INIT_COLLECTION, initCollection)
}

function* setCollectionView({viewId}) {
    yield call(api.setCollectionView, viewId)
}

function* watchSetCollectionView() {
    yield takeLatest(collection.types.SET_COLLECTION_VIEW, setCollectionView)
}

function* fetchRelations({id, pagetype}) {
    try {
        const data = yield call(api.fetchRelations, {id, pagetype})
        yield put(relations.actions.fetchRelationsSuccess(data))
    } catch (error) {
        yield put(relations.actions.fetchRelationsFailed())
    }
}

function* watchFetchRelations() {
    yield takeLatest(relations.types.RELATIONS_FETCH, fetchRelations)
}

function* createRelation({from, to, verb, title}) {
    try {
        const relation = yield call(api.createRelation, {from, to, verb, title})
        yield put(relations.actions.updateRelationSuccess(relation))
    } catch (error) {
        yield put(relations.actions.updateRelationFailed())
    }
}

function* watchCreateRelation() {
    yield takeEvery(relations.types.RELATION_CREATE, createRelation)
}

function* updateRelation({id, verb}) {
    try {
        const relation = yield call(api.updateRelation, {id, verb})
        yield put(relations.actions.updateRelationSuccess(relation))
    } catch (error) {
        yield put(relations.actions.updateRelationFailed())
    }
}

function* watchUpdateRelation() {
    yield takeEvery(relations.types.RELATION_UPDATE, updateRelation)
}

function* initRecord({entity, recordId}) {
    try {
        const data = yield call(api.initRecord, {entity, recordId})
        yield put(record.actions.recordSuccess(data))
    } catch (error) {
        yield put(record.actions.recordFailed())
    }
}

function* watchInitRecord() {
    yield takeLatest(record.types.FETCH_RECORD, initRecord)
}

function* duplicateRecord() {
    try {
        const data = yield call(api.duplicateRecord)
		yield put(messages.actions.messageSet(warning.duplicateRecordSuccess))
        yield put(record.actions.duplicateRecordSuccess(data))
    } catch (error) {
        yield put(record.actions.duplicateRecordFailed())
    }
}

function* watchDuplicateRecord() {
    yield takeEvery(record.types.DUPLICATE_RECORD, duplicateRecord)
}

function* deleteRecord({ids}) {
    try {
        const data = yield call(api.deleteRecord, ids)
        yield put(record.actions.deleteRecordSuccess(data))
    } catch (error) {
        yield put(record.actions.deleteRecordFailed())
    }
}

function* watchDeleteRecord() {
    yield takeEvery(record.types.DELETE_RECORD, deleteRecord)
}

function* updateRecord({recordId, fieldId, value}) {
    yield call(api.updateRecord, {recordId, fieldId, value})
}

function* watchUpdateRecord() {
    yield takeEvery(record.types.UPDATE_RECORD, updateRecord)
}

function* fetchRenameEntity({name, entity}) {
    try {
        yield call(api.fetchRenameEntity, {name, entity})
        yield put(entities.actions.fetchRenameEntitySuccess())
    } catch (error) {
        yield put(entities.actions.fetchRenameEntityFailed())
    }
}

function* watchFetchRenameEntity() {
    yield takeEvery(entities.types.RENAME_ENTITY_FETCH, fetchRenameEntity)
}

function* fetchRenameCol({name, col}) {
    try {
        yield call(api.fetchRenameCol, {name, col})
        yield put(entities.actions.fetchRenameColSuccess())
    } catch (error) {
        yield put(entities.actions.fetchRenameColFailed())
    }
}

function* watchFetchRenameCol() {
    yield takeEvery(entities.types.RENAME_COL_FETCH, fetchRenameCol)
}

function* fetchDeleteCol({col}) {
    try {
        yield call(api.fetchDeleteCol, col)
        yield put(entities.actions.fetchDeleteColSuccess())
    } catch (error) {
        yield put(entities.actions.fetchDeleteColFailed())
    }
}

function* watchFetchDeleteCol() {
    yield takeEvery(entities.types.DELETE_COL_FETCH, fetchDeleteCol)
}

function* fetchAddCol({entity}) {
    try {
        const col = yield call(api.fetchAddCol, entity)
        yield put(entities.actions.fetchAddColSuccess(col))
    } catch (error) {
        yield put(entities.actions.fetchAddColFailed())
    }
}

function* watchFetchAddCol() {
    yield takeEvery(entities.types.ADD_COL_FETCH, fetchAddCol)
}

function* fetchLockCol({field}) {
    try {
        yield call(api.fetchLockCol, {field})
        yield put(collection.actions.updateViewStateSuccess())
    } catch (error) {
        yield put(collection.actions.updateViewStateFailed())
    }
}

function* watchFetchLockCol() {
    yield takeEvery(collection.types.LOCK_COL_FETCH, fetchLockCol)
}

function* fetchDocumentAdd() {
    try {
        const data = yield call(api.fetchDocumentAdd)
        yield put(collection.actions.fetchDocumentAddSuccess(data))
    } catch (error) {
        yield put(collection.actions.fetchDocumentAddFailed())
    }
}

function* watchFetchDocumentAdd() {
    yield takeEvery(collection.types.DOCUMENT_ADD_FETCH, fetchDocumentAdd)
}

function* fetchDocumentDelete({records}) {
    try {
        yield call(api.fetchDocumentDelete, {records})
        yield put(collection.actions.fetchDocumentDeleteSuccess())
    } catch (error) {
        yield put(collection.actions.fetchDocumentDeleteFailed())
    }
}

function* watchFetchDocumentDelete() {
    yield takeEvery(collection.types.DOCUMENT_DELETE_FETCH, fetchDocumentDelete)
}

function* fetchDocumentUpdate({record, fieldId, value}) {
    yield call(api.fetchDocumentUpdate, {record, fieldId, value})
}

function* watchFetchDocumentUpdate() {
    yield takeEvery(collection.types.DOCUMENT_UPDATE_FETCH, fetchDocumentUpdate)
}

function* fetchDocumentsUpdate({records, fieldId, value}) {
    yield call(api.fetchDocumentsUpdate, {records, fieldId, value})
}

function* watchFetchDocumentsUpdate() {
    yield takeEvery(collection.types.DOCUMENTS_UPDATE_FETCH, fetchDocumentsUpdate)
}

function* fetchLinkedOptions({id}) {
    try {
        const data = yield call(api.fetchLinkedOptions, id)
        yield put(linkedfield.actions.fetchLinkedOptionsSuccess(data, id))
    } catch (error) {
        yield put(linkedfield.actions.fetchLinkedOptionsFailed())
    }
}

function* watchFetchLinkedOptions() {
    yield takeEvery(linkedfield.types.LINKED_OPTIONS_FETCH, fetchLinkedOptions)
}

function* setTextFilter({pattern}) {
    try {
        yield call(api.setTextFilter, pattern)
        yield put(collection.actions.setTextFilterSuccess())
    } catch (error) {
        yield put(collection.actions.setTextFilterFailed())
    }
}

function* watchSetTextFilter() {
    yield debounce(SEARCH_REQUEST_DELAY, collection.types.SET_TEXT_FILTER, setTextFilter)
}

function* createView({config, id, value}) {
    try {
        const view = value === undefined ? yield call(api.createView, {config, id}) :
            yield call(api.toggleIncludeView, {id, value})
        const data = yield call(api.fetchViews, {id})
		yield call(api.setCollectionView, view.id)
        yield put(collection.actions.createViewSuccess(data))
    } catch (error) {
        yield put(collection.actions.createViewFailed())
    }
}

function* watchCreateView() {
    yield takeEvery(collection.types.CREATE_VIEW_FETCH, createView)
}

function* deleteView({id, viewId, value}) {
    try {
        const view = value === undefined ? yield call(api.deleteView, {viewId}) :
            yield call(api.toggleIncludeView, {id, value})
        const data = yield call(api.fetchViews, {id})
		const current = data.find(item => item.isDefault).id
		yield call(api.setCollectionView, current)
        yield put(collection.actions.deleteViewSuccess(data, view))
    } catch (error) {
        yield put(collection.actions.deleteViewFailed())
    }
}

function* watchDeleteView() {
    yield takeEvery(collection.types.DELETE_VIEW_FETCH, deleteView)
}

function* updateView({config, id}) {
    try {
        yield call(api.updateView, {config, id})
        const data = yield call(api.fetchViews, {id})
        yield put(collection.actions.updateViewSuccess(data))
    } catch (error) {
        yield put(collection.actions.updateViewFailed())
    }
}

function* watchUpdateView() {
    yield takeEvery(collection.types.UPDATE_VIEW_FETCH, updateView)
}

function* cloneView({viewId, id}) {
    try {
        const view = yield call(api.cloneView, viewId, id)
        const data = yield call(api.fetchViews, {id})
		yield call(api.setCollectionView, view.id)
        yield put(collection.actions.cloneViewSuccess(data))
    } catch (error) {
        yield put(collection.actions.cloneViewFailed())
    }
}

function* watchCloneView() {
    yield takeEvery(collection.types.VIEW_CLONE_FETCH, cloneView)
}

function* reorderViews({id, order}) {
    try {
        yield call(api.reorderViews, {id, order})
        const data = yield call(api.fetchViews, {id})
        yield put(collection.actions.reorderViewsSuccess(data))
    } catch (error) {
        yield put(collection.actions.reorderViewsFailed())
    }
}

function* watchReorderViews() {
    yield takeLatest(collection.types.REORDER_VIEWS_FETCH, reorderViews)
}

function* toggleFieldSort({field}) {
    try {
        yield call(api.toggleFieldSort, {field})
        yield put(collection.actions.updateViewStateSuccess())
    } catch (error) {
        yield put(collection.actions.updateViewStateFailed())
    }
}

function* watchToggleFieldSort() {
    yield takeEvery(collection.types.TOGGLE_SORT_FETCH, toggleFieldSort)
}

function* toggleFieldHide({field}) {
    try {
        yield call(api.toggleFieldHide, {field})
        yield put(collection.actions.updateViewStateSuccess())
    } catch (error) {
        yield put(collection.actions.updateViewStateFailed())
    }
}

function* watchToggleFieldHide() {
    yield takeEvery(collection.types.TOGGLE_HIDE_FETCH, toggleFieldHide)
}

function* toggleReadonly({field}) {
    try {
        yield call(api.toggleReadonly, field)
        yield put(collection.actions.updateViewStateSuccess())
    } catch (error) {
        yield put(collection.actions.updateViewStateFailed())
    }
}

function* watchToggleReadonly() {
    yield takeEvery(collection.types.TOGGLE_READONLY, toggleReadonly)
}

function* changeTypeToBasic({field, fieldType}) {
    try {
        yield call(api.changeTypeToBasic, {field, fieldType})
        yield put(entities.actions.changeTypeSuccess())
    } catch (error) {
        yield put(entities.actions.changeTypeFailed())
        yield put(messages.actions.messageSet(warning.error))
    }
}

function* watchChangeTypeToBasic() {
    yield takeEvery(entities.types.CHANGE_TYPE_TO_BASIC, changeTypeToBasic)
}

function* changeTypeToComplex({field, config, format}) {
    try {
        yield call(api.changeTypeToComplex, {field, config, format})
        yield put(entities.actions.changeTypeSuccess())
    } catch (error) {
        yield put(entities.actions.changeTypeFailed())
        yield put(messages.actions.messageSet(warning.error))
    }
}

function* watchChangeTypeToComplex() {
    yield takeEvery(entities.types.CHANGE_TYPE_TO_COMPLEX, changeTypeToComplex)
}

function* setFilters({filters, save}) {
    try {
        yield call(api.setFilters, {filters, save})
        yield put(collection.actions.updateViewStateSuccess())
    } catch (error) {
        yield put(collection.actions.updateViewStateFailed())
        yield put(messages.actions.messageSet(warning.error))
    }
}

function* watchSetFilters() {
    yield takeEvery(collection.types.SET_FILTERS_FETCH, setFilters)
}

function* setDefaultValue({field, value}) {
    try {
        yield call(api.setDefaultValue, field, value)
        yield put(entities.actions.setDefaultValueSuccess())
    } catch (error) {
        yield put(entities.actions.setDefaultValueFailed())
    }
}

function* watchSetDefaultValue() {
    yield takeEvery(entities.types.SET_DEFAULT_VALUE, setDefaultValue)
}

function* fetchModules() {
    try {
        const data = yield call(api.fetchModules)
        yield put(modules.actions.fetchModulesSuccess(data))
    } catch (error) {
        yield put(modules.actions.fetchModulesFailed())
    }
}

function* watchFetchModules() {
    yield takeEvery(modules.types.MODULES_FETCH, fetchModules)
}

function* createModule({config}) {
    try {
        const item = yield call(api.createModule, config)
        const data = yield call(api.fetchModules)
        yield put(modules.actions.createModuleSuccess(item, data))
    } catch (error) {
        yield put(modules.actions.createModuleFailed())
        yield put(messages.actions.messageSet(warning.error))
    }
}

function* watchCreateModule() {
    yield takeEvery(modules.types.CREATE_MODULE, createModule)
}

function* deleteModule({id}) {
    try {
        const item = yield call(api.deleteModule, id)
        const data = yield call(api.fetchModules)
        yield put(modules.actions.deleteModuleSuccess(item, data))
    } catch (error) {
        yield put(modules.actions.deleteModuleFailed())
        yield put(messages.actions.messageSet(warning.error))
    }
}

function* watchDeleteModule() {
    yield takeEvery(modules.types.DELETE_MODULE, deleteModule)
}

function* updateModule({config}) {
    try {
        const item = yield call(api.updateModule, config)
        const data = yield call(api.fetchModules)
        yield put(modules.actions.updateModuleSuccess(item, data))
    } catch (error) {
        yield put(modules.actions.updateModuleFailed())
        yield put(messages.actions.messageSet(warning.error))
    }
}

function* watchUpdateModule() {
    yield takeEvery(modules.types.UPDATE_MODULE, updateModule)
}

function* changeModule({cardId, moduleId}) {
    try {
        const item = yield call(api.changeModule, {cardId, moduleId})
        const data = yield call(api.fetchModules)
        yield put(modules.actions.updateModuleSuccess(item, data))
    } catch (error) {
        yield put(modules.actions.updateModuleFailed())
    }
}

function* watchChangeModule() {
    yield takeLatest(modules.types.CHANGE_MODULE, changeModule)
}

function* setApplicationLogo({img}) {
    try {
        const logo = yield call(api.setApplicationLogo, {img})
        yield put(applications.actions.setApplicationLogoSuccess(logo))
    } catch (error) {
        yield put(applications.actions.setApplicationLogoFailed())
    }
}

function* watchSetApplicationLogo() {
    yield takeEvery(applications.types.SET_APPLICATION_LOGO, setApplicationLogo)
}

function* setApplicationName({name}) {
    try {
        yield call(api.setApplicationName, name)
        yield put(applications.actions.setApplicationNameSuccess())
    } catch (error) {
        yield put(applications.actions.setApplicationNameFailed())
    }
}

function* watchSetApplicationName() {
    yield takeEvery(applications.types.SET_APPLICATION_NAME, setApplicationName)
}

function* setUserData({userdata}) {
    try {
        const data = yield call(api.setUserData, userdata)
        yield put(login.actions.setUserDataSuccess(data))
    } catch (error) {
        if (error === flBusiness.Enum.ErrorCode.UserExists) {
            yield put(messages.actions.messageSet(warning.userNameExists))
        } else {
            yield put(messages.actions.messageSet(warning.error))
        }
        yield put(login.actions.setUserDataFailed())
    }
}

function* watchSetUserData() {
    yield takeEvery(login.types.SET_USER_DATA, setUserData)
}

function* createCard({config, format}) {
    try {
        yield call(api.createCard, {config, format})
		const data = flBusiness.Module.getAll()
        yield put(modules.actions.createCardSuccess(data))
    } catch (error) {
        yield put(modules.actions.createCardFailed())
        yield put(messages.actions.messageSet(warning.error))
    }
}

function* watchCreateCard() {
    yield takeEvery(modules.types.CREATE_CARD, createCard)
}

function* copyCard({id}) {
    try {
        yield call(api.copyCard, id)
		const data = flBusiness.Module.getAll()
        yield put(modules.actions.copyCardSuccess(data))
    } catch (error) {
        yield put(modules.actions.copyCardFailed())
        yield put(messages.actions.messageSet(warning.error))
    }
}

function* watchCopyCard() {
    yield takeEvery(modules.types.COPY_CARD, copyCard)
}

function* updateCard({config, format}) {
    try {
        const item = yield call(api.updateCard, {config, format})
        const data = yield call(api.fetchModules)
        yield put(modules.actions.updateCardSuccess(data, item))
    } catch (error) {
        yield put(modules.actions.updateCardFailed())
        yield put(messages.actions.messageSet(warning.error))
    }
}

function* watchUpdateCard() {
    yield takeEvery(modules.types.UPDATE_CARD, updateCard)
}

function* deleteCard({id}) {
    try {
        yield call(api.deleteCard, id)
		const data = flBusiness.Module.getAll()
        yield put(modules.actions.deleteCardSuccess(data, id))
    } catch (error) {
        yield put(modules.actions.deleteCardFailed())
        yield put(messages.actions.messageSet(warning.error))
    }
}

function* watchDeleteCard() {
    yield takeEvery(modules.types.DELETE_CARD, deleteCard)
}

function* getRollup({cardId}) {
    try {
        const rollup = yield call(api.getRollup, cardId)
        yield put(modules.actions.getRollupSuccess(rollup, cardId))
    } catch (error) {
        yield put(modules.actions.getRollupFailed())
    }
}

function* watchGetRollup() {
    yield takeEvery(modules.types.GET_ROLLUP, getRollup)
}

function* fetchReorderModules({order}) {
    try {
        const data = yield call(api.fetchReorderModules, order)
        yield put(modules.actions.fetchModulesSuccess(data))
    } catch (error) {
        yield put(modules.actions.fetchModulesFailed())
    }
}

function* watchFetchReorderModules() {
    yield takeLatest(modules.types.REORDER_MODULES, fetchReorderModules)
}

function* fetchPlanning({id}) {
    try {
        const planning = yield call(api.fetchPlanning, id)
        yield put(plannings.actions.fetchPlanningSuccess(planning))
    } catch (error) {
        yield put(plannings.actions.fetchPlanningFailed())
    }
}

function* watchFetchPlanning() {
    yield takeLatest(plannings.types.PLANNING_FETCH, fetchPlanning)
}

function* updatePlanning({config}) {
    try {
        const planning = yield call(api.updatePlanning, config)
        const data = yield call(api.fetchPlanningData, config.id)
		const modulesList = yield call(api.fetchModules)
        yield put(modules.actions.fetchModulesSuccess(modulesList))
        yield put(plannings.actions.updatePlanningSuccess(planning))
        yield put(plannings.actions.fetchPlanningDataSuccess(data))
    } catch (error) {
        yield put(plannings.actions.updatePlanningFailed())
    }
}

function* watchUpdatePlanning() {
    yield takeEvery(plannings.types.UPDATE_PLANNING, updatePlanning)
}


function* fetchPlanningData({id}) {
    try {
        const data = yield call(api.fetchPlanningData, id)
        yield put(plannings.actions.fetchPlanningDataSuccess(data))
    } catch (error) {
        yield put(plannings.actions.fetchPlanningDataFailed())
    }
}

function* watchFetchPlanningData() {
    yield takeLatest(plannings.types.PLANNING_DATA_FETCH, fetchPlanningData)
}

function* changePlanningEvent({entity, record, planning, action}) {
    try {
        if (action === operation.delete) {
            yield call(api.eventDelete, entity, record)
        } else if (action === operation.create) {
            yield call(api.eventCreate, entity, record)
        } else {
            yield call(api.eventUpdate, entity, record)
        }
        const data = yield call(api.fetchPlanningData, planning)
        yield put(plannings.actions.changePlanningEventSuccess(data))
    } catch (error) {
        yield put(plannings.actions.changePlanningEventFailed())
    }
}

function* watchChangePlanningEvent() {
    yield takeEvery(plannings.types.PLANNING_EVENT_CHANGE, changePlanningEvent)
}

function* setColors({id, data}) {
    try {
        const planning = yield call(api.setColors, {id, data})
        const info = yield call(api.fetchPlanningData, id)
        yield put(plannings.actions.setColorsSuccess(planning))
        yield put(plannings.actions.fetchPlanningDataSuccess(info))
    } catch (error) {
        yield put(plannings.actions.setColorsFailed())
    }
}

function* watchSetColors() {
    yield takeEvery(plannings.types.SET_COLORS, setColors)
}

function* fetchSheets({file}) {
    try {
        const data = yield call(api.getSheets, file)
        yield put(modules.actions.getSheetsSuccess(data))
    } catch (error) {
        yield put(modules.actions.getSheetsFailed())
        yield put(messages.actions.messageSet(warning.corruptedFile))
    }
}

function* watchFetchSheets() {
    yield takeEvery(modules.types.SHEETS_FETCH, fetchSheets)
}

function* importExcel({multiple, file, entityId, name, moduleId}) {
    try {
        const entity = yield call(api.excelParse, {multiple, file, entityId, name, moduleId})
		const data = yield call(api.fetchModules)
        yield put(modules.actions.importExcelSuccess(entity, data))
    } catch (error) {
        yield put(modules.actions.importExcelFailed())
        const failure = error === flBusiness.Enum.ErrorCode.ExcelImportNoMapping ?
            warning.excelImportNoMapping : warning.error
        yield put(messages.actions.messageSet(failure))
    }
}

function* watchImportExcel() {
    yield takeEvery(modules.types.EXCEL_FETCH, importExcel)
}

function* fetchExcelTemplates({entityId}) {
    try {
        const data = yield call(api.fetchExcelTemplates, entityId)
        yield put(excel.actions.fetchExcelTemplatesSuccess(data))
    } catch (error) {
        yield put(excel.actions.fetchExcelTemplatesFailed())
    }
}

function* watchExcelTemplates() {
    yield takeEvery(excel.types.EXCEL_TEMPLATES_FETCH, fetchExcelTemplates)
}

function* exportExcel({template, documents}) {
    try {
        yield call(api.exportExcel, {template, documents})
        yield put(excel.actions.exportExcelSuccess())
    } catch (error) {
        yield put(excel.actions.exportExcelFailed())
        yield put(messages.actions.messageSet(warning.error))
    }
}

function* watchExportExcel() {
    yield takeEvery(excel.types.EXCEL_EXPORT, exportExcel)
}

function* deleteExcelTemplate({template}) {
    try {
        const id = yield call(api.deleteExcelTemplate, template)
        yield put(excel.actions.deleteExcelTemplateSuccess(id))
        yield put(messages.actions.messageSet(warning.deletedExcelTemplate))
    } catch (error) {
        yield put(excel.actions.deleteExcelTemplateFailed())
        yield put(messages.actions.messageSet(warning.error))
    }
}

function* watchDeleteExcelTemplate() {
    yield takeEvery(excel.types.EXCEL_TEMPLATE_DELETE, deleteExcelTemplate)
}

function* excelBackup() {
    try {
        yield call(api.globalExportExcel)
        yield put(excel.actions.excelBackupSuccess())
    } catch (error) {
        yield put(excel.actions.excelBackupFailed())
        yield put(messages.actions.messageSet(warning.error))
    }
}

function* watchExcelBackup() {
    yield takeEvery(excel.types.EXCEL_BACKUP_FETCH, excelBackup)
}

function* fetchPublicEnabled() {
    try {
        const data = yield call(api.getPublicEnabled)
        yield put(applications.actions.fetchPublicEnabledSuccess(data))
    } catch (error) {
        yield put(applications.actions.fetchPublicEnabledFailed())
    }
}

function* watchFetchPublicEnabled() {
    yield takeLatest(applications.types.PUBLIC_ACCESS_FETCH, fetchPublicEnabled)
}


function* fetchPublicTables({entityId}) {
    try {
        const data = yield call(api.fetchPublicTables, entityId)
        yield put(publictables.actions.fetchPublicTablesSuccess(data))
    } catch (error) {
        yield put(publictables.actions.fetchPublicTablesFailed())
    }
}

function* watchFetchPublicTables() {
    yield takeEvery(publictables.types.PUBLIC_TABLES_FETCH, fetchPublicTables)
}

function* createPublicTable({data}) {
    try {
        yield call(api.createPublicTable, data)
        yield put(publictables.actions.createPublicTableSuccess())
    } catch (error) {
        yield put(publictables.actions.createPublicTableFailed())
        yield put(messages.actions.messageSet(warning.error))
    }
}

function* watchCreatePublicTable() {
    yield takeEvery(publictables.types.PUBLIC_TABLE_CREATE, createPublicTable)
}

function* deletePublicTable({table}) {
    try {
        const id = yield call(api.deletePublicTable, table)
        yield put(publictables.actions.deletePublicTableSuccess(id))
        yield put(messages.actions.messageSet(warning.deletedPublicTable))
    } catch (error) {
        yield put(publictables.actions.deletePublicTableFailed())
        yield put(messages.actions.messageSet(warning.error))
    }
}

function* watchDeletePublicTable() {
    yield takeEvery(publictables.types.PUBLIC_TABLE_DELETE, deletePublicTable)
}

function* updatePublicTable({data}) {
    try {
        yield call(api.updatePublicTable, data)
        yield put(publictables.actions.updatePublicTableSuccess())
    } catch (error) {
        yield put(publictables.actions.updatePublicTableFailed())
        yield put(messages.actions.messageSet(warning.error))
    }
}

function* watchUpdatePublicTable() {
    yield takeEvery(publictables.types.PUBLIC_TABLE_UPDATE, updatePublicTable)
}

function* createPublicForm({data, notifications}) {
    try {
        yield call(api.createPublicForm, data, notifications)
        yield put(publicforms.actions.createPublicFormSuccess())
    } catch (error) {
        yield put(publicforms.actions.createPublicFormFailed())
        yield put(messages.actions.messageSet(warning.error))
    }
}

function* watchCreatePublicForm() {
    yield takeEvery(publicforms.types.PUBLIC_FORM_CREATE, createPublicForm)
}

function* deletePublicForm({form}) {
    try {
        yield call(api.deletePublicForm, form)
        yield put(publicforms.actions.deletePublicFormSuccess())
        yield put(messages.actions.messageSet(warning.deletedPublicForm))
    } catch (error) {
        yield put(publicforms.actions.deletePublicFormFailed())
        yield put(messages.actions.messageSet(warning.error))
    }
}

function* watchDeletePublicForm() {
    yield takeEvery(publicforms.types.PUBLIC_FORM_DELETE, deletePublicForm)
}

function* updatePublicForm({data, notifications}) {
    try {
        yield call(api.updatePublicForm, data, notifications)
        yield put(publicforms.actions.updatePublicFormSuccess())
    } catch (error) {
        yield put(publicforms.actions.updatePublicFormFailed())
        yield put(messages.actions.messageSet(warning.error))
    }
}

function* watchUpdatePublicForm() {
    yield takeEvery(publicforms.types.PUBLIC_FORM_UPDATE, updatePublicForm)
}

function* clonePublicForm({form}) {
    try {
        const duplicate = yield call(api.clonePublicForm, form)
        yield put(publicforms.actions.clonePublicFormSuccess(duplicate))
    } catch (error) {
        yield put(publicforms.actions.clonePublicFormFailed())
        yield put(messages.actions.messageSet(warning.error))
    }
}

function* watchClonePublicForm() {
    yield takeEvery(publicforms.types.PUBLIC_FORM_CLONE, clonePublicForm)
}

function* createSmsTemplate({data}) {
    try {
        yield call(api.createSmsTemplate, data)
        yield put(sms.actions.createSmsTemplateSuccess())
    } catch (error) {
        yield put(sms.actions.createSmsTemplateFailed())
        yield put(messages.actions.messageSet(warning.error))
    }
}

function* watchCreateSmsTemplate() {
    yield takeEvery(sms.types.SMS_TEMPLATE_CREATE, createSmsTemplate)
}

function* deleteSmsTemplate({form}) {
    try {
        const id = yield call(api.deleteSmsTemplate, form)
        yield put(sms.actions.deleteSmsTemplateSuccess(id))
        yield put(messages.actions.messageSet(warning.deletedSmsTemplate))
    } catch (error) {
        yield put(sms.actions.deleteSmsTemplateFailed())
        yield put(messages.actions.messageSet(warning.error))
    }
}

function* watchDeleteSmsTemplate() {
    yield takeEvery(sms.types.SMS_TEMPLATE_DELETE, deleteSmsTemplate)
}

function* updateSmsTemplate({data}) {
    try {
        yield call(api.updateSmsTemplate, data)
        yield put(sms.actions.updateSmsTemplateSuccess())
    } catch (error) {
        yield put(sms.actions.updateSmsTemplateFailed())
        yield put(messages.actions.messageSet(warning.error))
    }
}

function* watchUpdateSmsTemplate() {
    yield takeEvery(sms.types.SMS_TEMPLATE_UPDATE, updateSmsTemplate)
}

function* sendSmsTemplate({template, documents, save, test}) {
    try {
        const result = yield call(api.sendSmsTemplate, template, documents, save, test)
        const keys = yield call(api.getKeysFeedback, template.entityId)
        yield put(sms.actions.sendSmsTemplateSuccess(result, test))
        yield put(statistics.actions.getKeysFeedbackSuccess(keys))
    } catch (error) {
        yield put(sms.actions.sendSmsTemplateFailed())
        yield put(messages.actions.messageSet(warning.error))
    }
}

function* watchSendSmsTemplate() {
    yield takeEvery(sms.types.SMS_TEMPLATE_SEND, sendSmsTemplate)
}

function* fetchSmsTemplate({id}) {
    try {
        const template = yield call(api.fetchSmsTemplate, id)
        yield put(sms.actions.getSmsTemplateSuccess(template))
    } catch (error) {
        yield put(sms.actions.getSmsTemplateFailed())
        yield put(messages.actions.messageSet(warning.error))
    }
}

function* watchFetchSmsTemplate() {
    yield takeEvery(sms.types.SMS_TEMPLATE_FETCH, fetchSmsTemplate)
}

function* createEmailTemplate({data}) {
    try {
        yield call(api.createEmailTemplate, data)
        yield put(email.actions.createEmailTemplateSuccess())
    } catch (error) {
        yield put(email.actions.createEmailTemplateFailed())
        yield put(messages.actions.messageSet(warning.error))
    }
}

function* watchCreateEmailTemplate() {
    yield takeEvery(email.types.EMAIL_TEMPLATE_CREATE, createEmailTemplate)
}

function* deleteEmailTemplate({id}) {
    try {
        yield call(api.deleteEmailTemplate, id)
        yield put(email.actions.deleteEmailTemplateSuccess())
        yield put(messages.actions.messageSet(warning.deletedEmailTemplate))
    } catch (error) {
        yield put(email.actions.deleteEmailTemplateFailed())
        yield put(messages.actions.messageSet(warning.error))
    }
}

function* watchDeleteEmailTemplate() {
    yield takeEvery(email.types.EMAIL_TEMPLATE_DELETE, deleteEmailTemplate)
}

function* cloneEmailTemplate({id}) {
    try {
        const duplicate = yield call(api.cloneEmailTemplate, id)
        yield put(email.actions.cloneEmailTemplateSuccess(duplicate))
    } catch (error) {
        yield put(email.actions.cloneEmailTemplateFailed())
        yield put(messages.actions.messageSet(warning.error))
    }
}

function* watchCloneEmailTemplate() {
    yield takeEvery(email.types.EMAIL_TEMPLATE_CLONE, cloneEmailTemplate)
}

function* updateEmailTemplate({data}) {
    try {
        yield call(api.updateEmailTemplate, data)
        yield put(email.actions.updateEmailTemplateSuccess())
    } catch (error) {
        yield put(email.actions.updateEmailTemplateFailed())
        yield put(messages.actions.messageSet(warning.error))
    }
}

function* watchUpdateEmailTemplate() {
    yield takeEvery(email.types.EMAIL_TEMPLATE_UPDATE, updateEmailTemplate)
}

function* sendEmailTemplate({template, documents, save, test}) {
    try {
        const result = yield call(api.sendEmailTemplate, template, documents, save, test)
        const keys = yield call(api.getKeysFeedback, template.entityId)
        yield put(email.actions.sendEmailTemplateSuccess(result, test))
        yield put(statistics.actions.getKeysFeedbackSuccess(keys))
    } catch (error) {
        yield put(email.actions.sendEmailTemplateFailed())
        yield put(messages.actions.messageSet(warning.error))
    }
}

function* watchSendEmailTemplate() {
    yield takeEvery(email.types.EMAIL_TEMPLATE_SEND, sendEmailTemplate)
}

function* fetchEmailTemplate({id}) {
    try {
        const template = yield call(api.fetchEmailTemplate, id)
        yield put(email.actions.getEmailTemplateSuccess(template))
    } catch (error) {
        yield put(email.actions.getEmailTemplateFailed())
        yield put(messages.actions.messageSet(warning.error))
    }
}

function* watchFetchEmailTemplate() {
    yield takeEvery(email.types.EMAIL_TEMPLATE_FETCH, fetchEmailTemplate)
}

function* fetchSignatures({application}) {
    try {
        const signatures = yield call(api.fetchSignatures)
        yield put(email.actions.getSignaturesSuccess(signatures, application))
    } catch (error) {
        yield put(email.actions.getSignaturesFailed())
    }
}

function* watchFetchSignatures() {
    yield takeLatest(email.types.SIGNATURES_FETCH, fetchSignatures)
}

function* createSender({sender, service}) {
    try {
        const data = yield call(api.createSender, {sender, service})
        yield put(applications.actions.createSenderSuccess(data))
    } catch (error) {
        yield put(applications.actions.createSenderFailed())
        yield put(messages.actions.messageSet(warning.verifyError))
    }
}

function* watchCreateSender() {
    yield takeEvery(applications.types.SENDER_CREATE, createSender)
}

function* deleteSender({id}) {
    try {
        yield call(api.deleteSender, id)
        yield put(applications.actions.deleteSenderSuccess())
    } catch (error) {
        yield put(applications.actions.deleteSenderFailed())
        yield put(messages.actions.messageSet(warning.error))
    }
}

function* watchDeleteSender() {
    yield takeEvery(applications.types.SENDER_DELETE, deleteSender)
}

function* verifySender({id}) {
    try {
        yield call(api.verifySender, id)
        yield put(applications.actions.verifySenderSuccess())
    } catch (error) {
        yield put(applications.actions.verifySenderFailed())
        yield put(messages.actions.messageSet(warning.error))
    }
}

function* watchVerifySender() {
    yield takeEvery(applications.types.SENDER_VERIFY, verifySender)
}

function* fetchUsers() {
    try {
        const users = yield call(api.fetchUsers)
        yield put(applications.actions.fetchUsersSuccess(users))
    } catch (error) {
        yield put(applications.actions.fetchUsersFailed())
    }
}

function* watchFetchUsers() {
    yield takeLatest(applications.types.USERS_FETCH, fetchUsers)
}

function* updateUser({obj}) {
    try {
        const user = yield call(api.updateUser, obj)
        yield put(applications.actions.updateUserSuccess(user))
    } catch (error) {
        yield put(applications.actions.updateUserFailed())
        yield put(messages.actions.messageSet(warning.error))
    }
}

function* watchUpdateUser() {
    yield takeEvery(applications.types.USER_UPDATE, updateUser)
}

function* deleteUser({name}) {
    try {
        const user = yield call(api.deleteUser, name)
        yield put(applications.actions.deleteUserSuccess(user))
    } catch (error) {
        yield put(applications.actions.deleteUserFailed())
        yield put(messages.actions.messageSet(warning.error))
    }
}

function* watchDeleteUser() {
    yield takeEvery(applications.types.USER_DELETE, deleteUser)
}

function* setOwner({name}) {
    try {
        const users = yield call(api.setOwner, name)
        yield put(applications.actions.setOwnerSuccess(users))
    } catch (error) {
        yield put(applications.actions.setOwnerFailed())
        yield put(messages.actions.messageSet(warning.error))
    }
}

function* watchSetOwner() {
    yield takeEvery(applications.types.SET_OWNER, setOwner)
}

function* changePassword({pass}) {
    try {
        yield call(api.changePassword, pass)
        yield put(login.actions.changePasswordSuccess())
    } catch (error) {
        yield put(login.actions.changePasswordFailed())
    }
}

function* watchChangePassword() {
    yield takeEvery(login.types.CHANGE_PASSWORD, changePassword)
}

function* deleteExportTemplate({id}) {
    try {
        yield call(api.deleteExportTemplate, id)
        yield put(exportdata.actions.deleteExportTemplateSuccess())
        yield put(messages.actions.messageSet(warning.deletedExportTemplate))
    } catch (error) {
        yield put(exportdata.actions.deleteExportTemplateFailed())
        yield put(messages.actions.messageSet(warning.error))
    }
}

function* watchDeleteExportTemplate() {
    yield takeEvery(exportdata.types.EXPORT_TEMPLATE_DELETE, deleteExportTemplate)
}

function* exportData({template, documents}) {
    try {
        yield call(api.exportData, {template, documents})
        yield put(exportdata.actions.exportDataSuccess())
    } catch (error) {
        yield put(exportdata.actions.exportDataFailed())
        yield put(messages.actions.messageSet(warning.error))
    }
}

function* watchExportData() {
    yield takeEvery(exportdata.types.EXPORT_DATA, exportData)
}

function* deleteEntity({id}) {
    try {
        const entity = yield call(api.deleteEntity, id)
        yield put(entities.actions.deleteEntitySuccess(entity))
    } catch (error) {
        yield put(entities.actions.deleteEntityFailed())
        yield put(messages.actions.messageSet(warning.error))
    }
}

function* watchDeleteEntity() {
    yield takeEvery(entities.types.ENTITY_DELETE, deleteEntity)
}

function* setFeedback({id}) {
    try {
        yield call(api.setFeedback, id)
        yield put(collection.actions.setFeedbackSuccess())
    } catch (error) {
        yield put(collection.actions.setFeedbackFailed())
    }
}

function* watchSetFeedback() {
    yield takeEvery(collection.types.SET_FEEDBACK, setFeedback)
}

function* getKeysFeedback({entity}) {
    try {
        const keys = yield call(api.getKeysFeedback, entity)
        yield put(statistics.actions.getKeysFeedbackSuccess(keys))
    } catch (error) {
        yield put(statistics.actions.getKeysFeedbackFailed())
    }
}

function* watchGetKeysFeedback() {
    yield takeLatest(statistics.types.KEYS_GET, getKeysFeedback)
}

function* deleteKey({id, entityId}) {
    try {
        yield call(api.deleteKey, id)
        const keys = yield call(api.getKeysFeedback, entityId)
        yield put(statistics.actions.deleteKeySuccess(keys))
    } catch (error) {
        yield put(statistics.actions.deleteKeyFailed())
    }
}

function* watchDeleteKey() {
    yield takeEvery(statistics.types.KEY_DELETE, deleteKey)
}

function* fetchStatistics({feedbacks}) {
    try {
        const data = yield call(api.fetchStatistics, feedbacks)
        yield put(statistics.actions.fetchStatisticsSuccess(data))
    } catch (error) {
        yield put(statistics.actions.fetchStatisticsFailed())
    }
}

function* watchFetchStatistics() {
    yield takeEvery(statistics.types.STATISTICS_FETCH, fetchStatistics)
}

function* updateSignature({obj, application}) {
    try {
        const signature = yield call(api.updateSignature, obj)
        yield put(email.actions.updateSignatureSuccess(signature, application))
    } catch (error) {
        yield put(email.actions.updateSignatureFailed())
        yield put(messages.actions.messageSet(warning.error))
    }
}

function* watchUpdateSignature() {
    yield takeEvery(email.types.SIGNATURE_UPDATE, updateSignature)
}

function* deleteSignature({id, application}) {
    try {
        const signature = yield call(api.deleteSignature, id)
        yield put(email.actions.deleteSignatureSuccess(signature, application))
    } catch (error) {
        yield put(email.actions.deleteSignatureFailed())
        yield put(messages.actions.messageSet(warning.error))
    }
}

function* watchDeleteSignature() {
    yield takeEvery(email.types.SIGNATURE_DELETE, deleteSignature)
}

function* createSignature({obj, application}) {
    try {
        const signature = yield call(api.createSignature, obj)
        yield put(email.actions.createSignatureSuccess(signature, application))
    } catch (error) {
        yield put(email.actions.createSignatureFailed())
        yield put(messages.actions.messageSet(warning.error))
    }
}

function* watchCreateSignature() {
    yield takeEvery(email.types.SIGNATURE_CREATE, createSignature)
}

function* getCallUrl() {
    try {
        const url = yield call(api.getCallUrl)
        yield put(login.actions.getCallUrlSuccess(url))
    } catch (error) {
        yield put(login.actions.getCallUrlFailed())
    }
}

function* watchGetCallUrl() {
    yield takeEvery(login.types.GET_CALL_URL, getCallUrl)
}

function* setCallUrl({value}) {
    try {
        const url = yield call(api.setCallUrl, value)
        yield put(login.actions.setCallUrlSuccess(url))
    } catch (error) {
        yield put(login.actions.setCallUrlFailed())
        yield put(messages.actions.messageSet(warning.error))
    }
}

function* watchSetCallUrl() {
    yield takeEvery(login.types.SET_CALL_URL, setCallUrl)
}

function* exportPdf({file, template, documents}) {
    try {
        yield call(api.exportPdf, {file, template, documents})
        yield put(pdf.actions.exportPdfSuccess())
    } catch (error) {
        yield put(pdf.actions.exportPdfFailed())
        yield put(messages.actions.messageSet(warning.error))
    }
}

function* watchExportPdf() {
    yield takeEvery(pdf.types.PDF_EXPORT, exportPdf)
}

function* deletePdfTemplate({id}) {
    try {
        yield call(api.deletePdfTemplate, id)
        yield put(pdf.actions.deletePdfTemplateSuccess())
    } catch (error) {
        yield put(pdf.actions.deletePdfTemplateFailed())
        yield put(messages.actions.messageSet(warning.error))
    }
}

function* watchDeletePdfTemplate() {
    yield takeEvery(pdf.types.PDF_TEMPLATE_DELETE, deletePdfTemplate)
}

function* getNotifications({formId}) {
    try {
        const notifications = yield call(api.getNotifications, formId)
        yield put(publicforms.actions.getNotificationsSuccess(notifications))
    } catch (error) {
        yield put(publicforms.actions.getNotificationsFailed())
        yield put(messages.actions.messageSet(warning.error))
    }
}

function* watchGetNotifications() {
    yield takeEvery(publicforms.types.NOTIFICATIONS_FETCH, getNotifications)
}

function* fetchFilterOptions({originEntity, foreignEntity}) {
    try {
        const filters = yield call(api.fetchFilterOptions, originEntity, foreignEntity)
        yield put(linkedfield.actions.fetchFilterOptionsSuccess(filters))
    } catch (error) {
        yield put(linkedfield.actions.fetchFilterOptionsFailed())
        yield put(messages.actions.messageSet(warning.error))
    }
}

function* watchFetchFilterOptions() {
    yield takeEvery(linkedfield.types.FILTER_OPTIONS_FETCH, fetchFilterOptions)
}

function* deletePlanning({id}) {
    try {
        yield call(api.deletePlanning, id)
        yield put(plannings.actions.deletePlanningSuccess())
    } catch (error) {
        yield put(plannings.actions.deletePlanningFailed())
        yield put(messages.actions.messageSet(warning.error))
    }
}

function* watchDeletePlanning() {
    yield takeEvery(plannings.types.PLANNING_DELETE, deletePlanning)
}

function* renameCard({id, string, card}) {
    try {
        yield call(api.renameCard, id, string, card)
		const data = yield call(api.fetchModules)
        yield put(modules.actions.renameCardSuccess(data))
    } catch (error) {
        yield put(modules.actions.renameCardFailed())
        yield put(messages.actions.messageSet(warning.error))
    }
}

function* watchRenameCard() {
    yield takeLatest(modules.types.RENAME_CARD_FETCH, renameCard)
}

function* fetchScheduler({id}) {
    try {
        const data = yield call(api.fetchScheduler, id)
        yield put(scheduler.actions.fetchSchedulerSuccess(data))
    } catch (error) {
        yield put(scheduler.actions.fetchSchedulerFailed())
    }
}

function* watchFetchScheduler() {
    yield takeLatest(scheduler.types.SCHEDULER_FETCH, fetchScheduler)
}

function* updateScheduler({config}) {
    try {
        const planning = yield call(api.updateScheduler, config)
        const data = yield call(api.fetchSchedulerData, config.id)
		const modulesList = yield call(api.fetchModules)
        yield put(modules.actions.fetchModulesSuccess(modulesList))
        yield put(scheduler.actions.updateSchedulerSuccess(planning))
        yield put(scheduler.actions.fetchSchedulerDataSuccess(data))
    } catch (error) {
        yield put(scheduler.actions.updateSchedulerFailed())
    }
}

function* watchUpdateScheduler() {
    yield takeEvery(scheduler.types.UPDATE_SCHEDULER, updateScheduler)
}

function* fetchSchedulerData({id}) {
    try {
        const data = yield call(api.fetchSchedulerData, id)
        yield put(scheduler.actions.fetchSchedulerDataSuccess(data))
    } catch (error) {
        yield put(scheduler.actions.fetchSchedulerDataFailed())
    }
}

function* watchFetchSchedulerData() {
    yield takeLatest(scheduler.types.SCHEDULER_DATA_FETCH, fetchSchedulerData)
}

function* setSchedulerColors({id, data}) {
    try {
        const planning = yield call(api.setSchedulerColors, {id, data})
        const info = yield call(api.fetchSchedulerData, id)
        yield put(scheduler.actions.setSchedulerColorsSuccess(planning))
        yield put(scheduler.actions.fetchSchedulerDataSuccess(info))
    } catch (error) {
        yield put(scheduler.actions.setSchedulerColorsFailed())
    }
}

function* watchSetSchedulerColors() {
    yield takeEvery(scheduler.types.SET_SCHEDULER_COLORS, setSchedulerColors)
}

function* deleteScheduler({id}) {
    try {
        yield call(api.deleteScheduler, id)
        yield put(scheduler.actions.deleteSchedulerSuccess())
    } catch (error) {
        yield put(scheduler.actions.deleteSchedulerFailed())
        yield put(messages.actions.messageSet(warning.error))
    }
}

function* watchDeleteScheduler() {
    yield takeEvery(scheduler.types.SCHEDULER_DELETE, deleteScheduler)
}

function* changeSchedulerEvent({entity, record, planning, action}) {
    try {
        if (action === operation.delete) {
            yield call(api.eventDelete, entity, record)
        } else if (action === operation.create) {
            yield call(api.eventCreate, entity, record)
        } else {
            yield call(api.eventUpdate, entity, record)
        }
        const data = yield call(api.fetchSchedulerData, planning)
        yield put(scheduler.actions.changeSchedulerEventSuccess(data))
    } catch (error) {
        yield put(scheduler.actions.changeSchedulerEventFailed())
    }
}

function* watchChangeSchedulerEvent() {
    yield takeEvery(scheduler.types.SCHEDULER_EVENT_CHANGE, changeSchedulerEvent)
}

function* setEmailTemplatesOrder({entity, order}) {
    yield call(api.setEmailTemplatesOrder, entity, order)
}

function* watchSetEmailTemplatesOrder() {
    yield takeEvery(email.types.SET_EMAIL_TEMPLATES_ORDER, setEmailTemplatesOrder)
}

function* setSmsTemplatesOrder({entity, order}) {
    yield call(api.setSmsTemplatesOrder, entity, order)
}

function* watchSetSmsTemplatesOrder() {
    yield takeEvery(sms.types.SET_SMS_TEMPLATES_ORDER, setSmsTemplatesOrder)
}

function* setExcelTemplatesOrder({entity, order}) {
    yield call(api.setExcelTemplatesOrder, entity, order)
}

function* watchSetExcelTemplatesOrder() {
    yield takeEvery(excel.types.SET_EXCEL_TEMPLATES_ORDER, setExcelTemplatesOrder)
}

function* setPublicTableTemplatesOrder({entity, order}) {
    yield call(api.setPublicTableTemplatesOrder, entity, order)
}

function* watchSetPublicTableTemplatesOrder() {
    yield takeEvery(publictables.types.SET_PUBLIC_TABLE_TEMPLATES_ORDER, setPublicTableTemplatesOrder)
}

function* setPublicFormTemplatesOrder({entity, order}) {
    yield call(api.setPublicFormTemplatesOrder, entity, order)
}

function* watchSetPublicFormTemplatesOrder() {
    yield takeEvery(publicforms.types.SET_PUBLIC_FORM_TEMPLATES_ORDER, setPublicFormTemplatesOrder)
}

function* setExportDataTemplatesOrder({entity, order}) {
    yield call(api.setExportDataTemplatesOrder, entity, order)
}

function* watchSetExportDatatTemplatesOrder() {
    yield takeEvery(exportdata.types.SET_EXPORT_DATA_TEMPLATES_ORDER, setExportDataTemplatesOrder)
}

function* setPdfTemplatesOrder({entity, order}) {
    yield call(api.setPdfTemplatesOrder, entity, order)
}

function* watchSetPdfTemplatesOrder() {
    yield takeEvery(pdf.types.SET_PDF_TEMPLATES_ORDER, setPdfTemplatesOrder)
}

function initUpdateDictionary() {
    return eventChannel(emitter => {
        flBusiness.Notification.onDictionaryMessage((data) => {
            if (data) {
                return emitter(entities.actions.fetchEntitiesSuccess(data))
            }
        })
        // unsubscribe function
        return () => {
          console.log('unsubscribe')
        }
    })
}

function* watchUpdateDictionary() {
    const channel = yield call(initUpdateDictionary)

    while (true) {
        const action = yield take(channel)
        yield put(action)
      }
}

function messageCollection() {
    return eventChannel(emitter => {
        flBusiness.Notification.onCollectionMessage((data) => {
            if (data) {
                return emitter(collection.actions.collectionSuccess(data))
            }
        })
        // unsubscribe function
        return () => {
          console.log('unsubscribe')
        }
    })
}

function* watchMessageCollection() {
    const channel = yield call(messageCollection)

    while (true) {
        const action = yield take(channel)
        yield put(action)
      }
}

function messageRecord() {
    return eventChannel(emitter => {
        flBusiness.Notification.onRecordMessage((data) => {
            if (data) {
                return emitter(record.actions.recordSuccess(data))
            }
        })
        // unsubscribe function
        return () => {
          console.log('unsubscribe')
        }
    })
}

function* watchMessageRecord() {
    const channel = yield call(messageRecord)

    while (true) {
        const action = yield take(channel)
        yield put(action)
      }
}

export default function* rootSaga() {
    try {
        yield all([
            fork(watchFetchLogin),
            fork(watchFetchCallUsers),
            fork(watchFetchLogout),
            fork(watchInitCollection),
            fork(watchSetCollectionView),
            fork(watchFetchResizeColumn),
            fork(watchFetchReorderColumns),
            fork(watchInitRecord),
            fork(watchUpdateRecord),
            fork(watchMessageRecord),
            fork(watchFetchRenameEntity),
            fork(watchFetchRenameCol),
            fork(watchFetchLockCol),
            fork(watchFetchDocumentAdd),
            fork(watchFetchDocumentDelete),
            fork(watchFetchDocumentUpdate),
            fork(watchFetchInstances),
            fork(watchFetchAutologin),
            fork(watchFetchChangeApplication),
            fork(watchFetchRegistration),
            fork(watchUpdateDictionary),
            fork(watchFetchDeleteCol),
            fork(watchFetchAddCol),
            fork(watchSetTextFilter),
            fork(watchCreateView),
            fork(watchDeleteView),
            fork(watchUpdateView),
            fork(watchToggleFieldSort),
            fork(watchToggleFieldHide),
            fork(watchChangeTypeToBasic),
            fork(watchChangeTypeToComplex),
            fork(watchSetFilters),
            fork(watchFetchLinkedOptions),
            fork(watchMessageCollection),
            fork(watchFetchModules),
            fork(watchCreateModule),
            fork(watchDeleteModule),
            fork(watchUpdateModule),
            fork(watchSetApplicationLogo),
            fork(watchSetApplicationName),
            fork(watchSetUserData),
            fork(watchCreateCard),
            fork(watchUpdateCard),
            fork(watchDeleteCard),
            fork(watchFetchRelations),
            fork(watchChangeModule),
            fork(watchGetRollup),
            fork(watchFetchReorderModules),
            fork(watchReorderViews),
            fork(watchFetchPlanning),
            fork(watchUpdatePlanning),
            fork(watchFetchPlanningData),
            fork(watchChangePlanningEvent),
            fork(watchSetColors),
            fork(watchDuplicateRecord),
            fork(watchDeleteRecord),
            fork(watchFetchDocumentsUpdate),
            fork(watchCreateRelation),
            fork(watchUpdateRelation),
            fork(watchFetchSheets),
            fork(watchImportExcel),
            fork(watchExcelTemplates),
            fork(watchExportExcel),
            fork(watchDeleteExcelTemplate),
            fork(watchExcelBackup),
            fork(watchSetDefaultValue),
            fork(watchFetchPublicEnabled),
            fork(watchFetchPublicTables),
            fork(watchCreatePublicTable),
            fork(watchDeletePublicTable),
            fork(watchUpdatePublicTable),
            fork(watchCreatePublicForm),
            fork(watchDeletePublicForm),
            fork(watchUpdatePublicForm),
            fork(watchCreateSmsTemplate),
            fork(watchDeleteSmsTemplate),
            fork(watchUpdateSmsTemplate),
            fork(watchSendSmsTemplate),
            fork(watchFetchSmsTemplate),
            fork(watchCreateEmailTemplate),
            fork(watchDeleteEmailTemplate),
            fork(watchUpdateEmailTemplate),
            fork(watchSendEmailTemplate),
            fork(watchFetchEmailTemplate),
            fork(watchCreateSender),
            fork(watchDeleteSender),
            fork(watchVerifySender),
            fork(watchFetchUsers),
            fork(watchUpdateUser),
            fork(watchDeleteUser),
            fork(watchChangePassword),
            fork(watchFetchSignatures),
            fork(watchDeleteExportTemplate),
            fork(watchExportData),
            fork(watchDeleteEntity),
            fork(watchSetFeedback),
            fork(watchDeleteKey),
            fork(watchFetchStatistics),
            fork(watchUpdateSignature),
            fork(watchDeleteSignature),
            fork(watchCreateSignature),
            fork(watchGetCallUrl),
            fork(watchSetCallUrl),
            fork(watchGetKeysFeedback),
            fork(watchExportPdf),
            fork(watchDeletePdfTemplate),
            fork(watchGetNotifications),
            fork(watchCopyCard),
			fork(watchFetchFilterOptions),
			fork(watchClonePublicForm),
			fork(watchCloneView),
			fork(watchCloneEmailTemplate),
			fork(watchSetOwner),
			fork(watchDeletePlanning),
			fork(watchRenameCard),
			fork(watchToggleReadonly),
            fork(watchFetchScheduler),
            fork(watchUpdateScheduler),
            fork(watchFetchSchedulerData),
            fork(watchDeleteScheduler),
            fork(watchSetSchedulerColors),
            fork(watchChangeSchedulerEvent),
            fork(watchSetEmailTemplatesOrder),
            fork(watchSetSmsTemplatesOrder),
            fork(watchSetExcelTemplatesOrder),
            fork(watchSetPublicTableTemplatesOrder),
            fork(watchSetPublicFormTemplatesOrder),
            fork(watchSetExportDatatTemplatesOrder),
            fork(watchSetPdfTemplatesOrder)
        ])
    } catch (err) {
        console.log(err)
    }
}
